import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtBR, fmtMoney } from '../lib/format';
import { statusBadge, classifBadge, catPath } from '../lib/ui';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';
import type { ContaAPagar, Retencao, TipoImposto } from '../types';

const TABS = [
  { id: 'dados', label: 'Dados' },
  { id: 'retencoes', label: 'Retenções' },
  { id: 'pagamento', label: 'Pagamento' },
  { id: 'documentos', label: 'Documentos' },
];

const num = (s: string) => { const n = parseFloat((s || '').replace(/\./g, '').replace(',', '.')); return isNaN(n) ? 0 : n; };

function openPdf(base64: string, mime: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: mime || 'application/pdf' }));
  window.open(url, '_blank');
}

export function DetalheConta({ navigate, params }: PageProps) {
  const id = Number(params.id);
  const [tab, setTab] = useState<string>((params.tab as string) ?? 'dados');
  const conta = useFetch(() => api.getConta(id), [id]);

  if (conta.loading) return <div className="page detail"><div className="loading">Carregando…</div></div>;
  if (conta.error || !conta.data) return <div className="page detail"><div className="empty">Conta não encontrada.</div></div>;

  const c = conta.data;
  const st = statusBadge(c);
  const cl = classifBadge(c.classificacao_status);

  return (
    <div className="page detail">
      <a className="back-link" onClick={() => navigate('contas')}>← Contas a Pagar</a>
      <div className="row wrap" style={{ alignItems: 'baseline', gap: 14, margin: '10px 0 4px' }}>
        <h1 style={{ margin: 0 }}>{c.fornecedor}</h1>
        <span className={`badge ${st.cls}`}>{st.label}</span>
        <span className={`badge ${cl.cls}`}>{cl.label}</span>
        <span className="mono" style={{ marginLeft: 'auto', fontSize: 20, fontWeight: 600, color: 'var(--ink)' }}>{fmtMoney(c.valor_liquido)}</span>
      </div>
      <div className="text-sm" style={{ color: 'var(--muted-2)', marginBottom: 18 }}>
        {c.descricao ?? '—'}{c.numero_doc ? ` · NF ${c.numero_doc}` : ''}{c.origem ? ` · via ${c.origem}` : ''}
      </div>

      <div className="tabs">
        {TABS.map((t) => <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>

      {tab === 'dados' && <DadosTab c={c} navigate={navigate} />}
      {tab === 'retencoes' && <RetencoesTab c={c} onSaved={() => { conta.refetch(); }} navigate={navigate} />}
      {tab === 'pagamento' && <PagamentoTab c={c} onPaid={() => conta.refetch()} />}
      {tab === 'documentos' && <DocumentosTab c={c} onChange={() => conta.refetch()} />}
    </div>
  );
}

function DadosTab({ c, navigate }: { c: ContaAPagar; navigate: PageProps['navigate'] }) {
  const campos: [string, React.ReactNode][] = [
    ['Fornecedor', c.fornecedor_id ? <a onClick={() => navigate('fornecedores', { id: c.fornecedor_id })}>{c.fornecedor}</a> : c.fornecedor],
    ['CNPJ / CPF', c.cnpj_cpf ?? '—'],
    ['Nº documento', c.numero_doc ?? '—'],
    ['Tipo', c.tipo === 'receita' ? 'Receita' : 'Despesa'],
    ['Vencimento', fmtBR(c.vencimento)],
    ['Forma de pagamento', c.forma_pagamento ?? '—'],
    ['Categoria', catPath(c)],
    ['Origem', c.origem ?? '—'],
    ['Valor bruto', <span className="mono">{fmtMoney(c.valor)}</span>],
    ['Retenções', <span className="mono">{fmtMoney(c.total_retencoes)}</span>],
    ['Valor líquido', <span className="mono">{fmtMoney(c.valor_liquido)}</span>],
    ['Quadra / lote', c.quadra_lote ?? '—'],
  ];
  return (
    <div className="card pad">
      <div className="kv-grid">
        {campos.map(([k, v], i) => (
          <div className="kv" key={i}><div className="k">{k}</div><div className="v">{v}</div></div>
        ))}
      </div>
    </div>
  );
}

const ALIQ: Record<string, { tipo: TipoImposto; aliq: number }> = {
  ISS: { tipo: 'ISS', aliq: 5 }, IRRF: { tipo: 'IRRF', aliq: 1.5 }, INSS: { tipo: 'INSS', aliq: 11 },
};

function RetencoesTab({ c, onSaved, navigate }: { c: ContaAPagar; onSaved: () => void; navigate: PageProps['navigate'] }) {
  const toast = useToast();
  const [rows, setRows] = useState<Array<Pick<Retencao, 'tipo_imposto' | 'aliquota' | 'valor'> & { guia_conta_id?: number | null }>>(
    (c.retencoes ?? []).map((r) => ({ tipo_imposto: r.tipo_imposto, aliquota: r.aliquota, valor: r.valor, guia_conta_id: r.guia_conta_id })),
  );
  const [busy, setBusy] = useState(false);
  const bruto = c.valor ?? 0;
  const totRet = rows.reduce((s, r) => s + r.valor, 0);
  const liq = bruto - totRet;

  const add = (key: string) => {
    const { tipo, aliq } = ALIQ[key];
    const valor = Math.round(bruto * aliq) / 100;
    setRows((rs) => [...rs, { tipo_imposto: tipo, aliquota: aliq, valor }]);
  };
  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const salvar = async () => {
    setBusy(true);
    try {
      await api.putRetencoes(c.id, rows.map((r) => ({ tipo_imposto: r.tipo_imposto, valor: r.valor, aliquota: r.aliquota ?? undefined })));
      toast('Retenções salvas. Guia do mês recalculada.');
      onSaved();
    } catch (e) {
      const body = (e as { body?: { error?: string } }).body;
      toast(body?.error === 'retencoes_maiores_que_valor' ? 'Retenções maiores que o valor bruto.' : 'Falha ao salvar retenções.', 'err');
    }
    setBusy(false);
  };

  return (
    <div className="card pad">
      {rows.length === 0 ? (
        <div className="text-sm muted" style={{ padding: '8px 0 14px' }}>Nenhuma retenção nesta conta.</div>
      ) : (
        rows.map((r, i) => (
          <div className="row" key={i} style={{ gap: 14, padding: '10px 0', borderBottom: '1px solid var(--border-soft)' }}>
            <span className="tag-tax" style={{ width: 64 }}>{r.tipo_imposto}</span>
            <span className="text-sm muted" style={{ width: 90 }}>{r.aliquota != null ? `${r.aliquota}%` : '—'}</span>
            <span className="mono cell-strong" style={{ flex: 1 }}>{fmtMoney(r.valor)}</span>
            {r.guia_conta_id && <a className="text-sm" onClick={() => navigate('guias')}>ver guia →</a>}
            <button className="btn danger-link text-sm" onClick={() => remove(i)}>remover</button>
          </div>
        ))
      )}
      <div className="row" style={{ gap: 8, margin: '14px 0 18px' }}>
        <button className="btn ghost xs" onClick={() => add('ISS')}>+ ISS 5%</button>
        <button className="btn ghost xs" onClick={() => add('IRRF')}>+ IRRF 1,5%</button>
        <button className="btn ghost xs" onClick={() => add('INSS')}>+ INSS 11%</button>
      </div>
      <div className="resumo-box">
        <span>Bruto <b>{fmtMoney(bruto)}</b></span>
        <span>− Retenções <b style={{ color: 'var(--danger)' }}>{fmtMoney(totRet)}</b></span>
        <span>= Líquido a pagar <b style={{ color: 'var(--primary)' }}>{fmtMoney(liq)}</b></span>
        <button className="btn primary sm" style={{ marginLeft: 'auto' }} onClick={salvar} disabled={busy}>Salvar retenções</button>
      </div>
      <div className="note">A guia do imposto do mês é criada/recalculada automaticamente ao salvar retenções.</div>
    </div>
  );
}

function PagamentoTab({ c, onPaid }: { c: ContaAPagar; onPaid: () => void }) {
  const toast = useToast();
  const bancos = useFetch(() => api.listContasBancarias(), []);
  const [multa, setMulta] = useState('');
  const [juros, setJuros] = useState('');
  const [desconto, setDesconto] = useState('');
  const [banco, setBanco] = useState<number | ''>('');
  const [pagoEm, setPagoEm] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const liq = c.valor_liquido ?? c.valor ?? 0;
  const esperado = liq + num(multa) + num(juros) - num(desconto);

  if (c.status === 'pago') {
    return (
      <div className="card pad" style={{ maxWidth: 560 }}>
        <div className="badge pago" style={{ padding: '14px 16px', fontSize: 14 }}>
          Pago em {fmtBR(c.pago_em)} · {fmtMoney(c.valor_pago ?? liq)}
        </div>
      </div>
    );
  }

  const pagar = async () => {
    setBusy(true);
    try {
      await api.pagarConta(c.id, {
        pago_em: pagoEm, conta_bancaria_id: banco ? Number(banco) : undefined,
        multa: num(multa), juros: num(juros), desconto: num(desconto), valor_pago: esperado,
      });
      toast('Pagamento confirmado.');
      onPaid();
    } catch (e) {
      const body = (e as { body?: { error?: string; esperado?: number } }).body;
      toast(body?.error === 'valores_inconsistentes' ? `Valores não fecham (esperado ${fmtMoney(body.esperado ?? 0)}).` : 'Falha ao registrar pagamento.', 'err');
      setBusy(false);
    }
  };

  return (
    <div className="card pad" style={{ maxWidth: 560 }}>
      <div className="grid-3" style={{ marginBottom: 16 }}>
        <div><label className="flabel">Multa</label><input className="input mono" placeholder="0,00" value={multa} onChange={(e) => setMulta(e.target.value)} /></div>
        <div><label className="flabel">Juros</label><input className="input mono" placeholder="0,00" value={juros} onChange={(e) => setJuros(e.target.value)} /></div>
        <div><label className="flabel">Desconto</label><input className="input mono" placeholder="0,00" value={desconto} onChange={(e) => setDesconto(e.target.value)} /></div>
      </div>
      <div className="info-box" style={{ marginBottom: 16 }}>
        Líquido {fmtMoney(liq)} + multa + juros − desconto = <b className="mono" style={{ color: 'var(--ink)', fontSize: 15 }}>{fmtMoney(esperado)}</b>
      </div>
      <div className="row" style={{ gap: 10 }}>
        <input type="date" className="input mono sm" style={{ width: 'auto' }} value={pagoEm} onChange={(e) => setPagoEm(e.target.value)} />
        <select className="select sm" style={{ width: 'auto', flex: 1 }} value={banco} onChange={(e) => setBanco(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Conta bancária…</option>
          {(bancos.data?.items ?? []).map((b) => <option key={b.id} value={b.id}>{b.apelido}</option>)}
        </select>
        <button className="btn primary" onClick={pagar} disabled={busy}>Confirmar pagamento</button>
      </div>
    </div>
  );
}

function DocumentosTab({ c, onChange }: { c: ContaAPagar; onChange: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const docs = [
    { key: 'nf', label: 'Nota fiscal', has: !!c.documentos?.nf },
    { key: 'boleto', label: 'Boleto', has: !!c.documentos?.boleto },
    { key: 'comprovante', label: 'Comprovante', has: !!c.documentos?.comprovante },
  ];

  const verPdf = async () => {
    try {
      const r = await api.getContaAnexo(c.id);
      if ('pdf_base64' in r) openPdf(r.pdf_base64, r.mime);
      else toast(r.email_link ? 'Anexo não salvo — abra pelo e-mail.' : 'Anexo indisponível.', 'err');
    } catch { toast('Falha ao abrir PDF.', 'err'); }
  };

  const anexar = (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    api.uploadContaAnexo(c.id, file)
      .then(() => { toast('Arquivo anexado.'); onChange(); })
      .catch(() => toast('Falha no upload.', 'err'))
      .finally(() => setBusy(false));
  };

  const dispensar = (v: boolean) => {
    setBusy(true);
    api.editarConta(c.id, { nf_dispensada: v })
      .then(() => { toast(v ? 'NF dispensada — deixa de aparecer nos alertas.' : 'NF volta a ser exigida.'); onChange(); })
      .catch(() => toast('Falha ao atualizar NF.', 'err'))
      .finally(() => setBusy(false));
  };

  // estado da NF (nf_status vem do backend; fallback deriva de anexo/dispensada)
  const nfStatus = c.nf_status ?? (c.documentos?.nf ? 'recebida' : c.nf_dispensada ? 'dispensada' : 'pendente');

  return (
    <div>
      {nfStatus !== 'recebida' && (
        <div className="card pad" style={{ maxWidth: 760, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
          background: nfStatus === 'pendente' ? 'var(--amber-bg, #fef3c7)' : 'var(--bg-2, #f3f4f6)' }}>
          <span className="cell-strong text-sm" style={{ flex: 1, color: nfStatus === 'pendente' ? 'var(--amber, #b45309)' : 'var(--text-2)' }}>
            {nfStatus === 'pendente'
              ? (c.status === 'pago' ? 'Paga sem nota fiscal — aguardando a NF chegar.' : 'Sem nota fiscal registrada.')
              : 'NF dispensada — esta despesa não terá nota fiscal.'}
          </span>
          {nfStatus === 'pendente'
            ? <button className="btn ghost xs" disabled={busy} onClick={() => dispensar(true)}>Dispensar NF</button>
            : <button className="btn ghost xs" disabled={busy} onClick={() => dispensar(false)}>Voltar a exigir NF</button>}
        </div>
      )}
      <div className="text-sm" style={{ color: 'var(--muted-2)', marginBottom: 14, maxWidth: 760 }}>
        Os documentos chegam automaticamente por e-mail/WhatsApp. Se algum não veio, anexe manualmente aqui (PDF, PNG ou JPEG).
      </div>
      <div className="grid-3" style={{ maxWidth: 760 }}>
        {docs.map((d) => (
          <div className="card pad" key={d.key} style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="row" style={{ gap: 6, marginBottom: 10 }}>
              <span className="cell-strong text-sm" style={{ flex: 1, color: 'var(--ink)' }}>{d.label}</span>
              {d.has && <span className="badge pago" style={{ fontSize: 10 }}>OK</span>}
            </div>
            <div className="info-box text-sm" style={{ textAlign: 'center', color: d.has ? 'var(--text-2)' : 'var(--muted)' }}>
              {d.has ? 'Documento disponível' : 'Não recebido'}
            </div>
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              {d.has && <button className="btn ghost xs" style={{ flex: 1 }} onClick={verPdf}>Visualizar</button>}
              <label className="btn dark xs" style={{ flex: 1, cursor: 'pointer' }}>
                {d.has ? 'Substituir' : 'Anexar'}
                <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => anexar(e.target.files?.[0])} disabled={busy} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
